// Loads the console's data from a source and keeps it fresh: on every
// Realtime change to orders or positions, every 30 s as a safety net, as soon
// as the window comes back to the front after more than 30 s, and when the
// connection comes back. A refresh keeps the figures on screen; one that fails
// keeps them too and raises `failed`, so the page can say « not refreshed »
// instead of quietly showing old numbers as new.
import { useCallback, useEffect, useRef, useState } from 'react';
import type { Source } from './api';
import { track } from './progress';
import type { Live, OpsOrder, Overview } from './types';

export interface Data {
  overview: Overview | null;
  orders: OpsOrder[] | null;
  live: Live | null;
  error: string | null;
  updatedAt: Date | null;
  /** How many of the first load's three reads have answered (0 to 3), for the splash's bar. */
  parts: number;
  /** A refresh is running while the figures are on screen. */
  refreshing: boolean;
  /** The last refresh failed: the figures on screen are from `updatedAt`. */
  failed: boolean;
  /** « Réessayer » is running after the first load failed. */
  retrying: boolean;
  /** A silent refresh (after an action, Realtime). */
  reload(): void;
  /** A refresh someone asked for: it moves the bar at the top. */
  retry(): void;
}

/** What a page needs to say whether its figures are being refreshed, or failed to be. */
export type Sync = Pick<Data, 'refreshing' | 'failed' | 'retry'>;

const STALE_MS = 30_000;

export function useData(src: Source): Data {
  const [overview, setOverview] = useState<Overview | null>(null);
  const [orders, setOrders] = useState<OpsOrder[] | null>(null);
  const [live, setLive] = useState<Live | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [updatedAt, setUpdatedAt] = useState<Date | null>(null);
  const [parts, setParts] = useState(0);
  const [running, setRunning] = useState(0);
  const first = useRef(true);
  const seq = useRef(0);
  const at = useRef(0);

  const load = useCallback((visible: boolean) => {
    const n = ++seq.current;
    setRunning((r) => r + 1);
    // Only the first load moves the splash's bar, one step per read that answers.
    const tick = <T,>(p: Promise<T>) => (first.current ? p.then((v) => { setParts((x) => Math.min(3, x + 1)); return v; }) : p);
    const all = Promise.all([tick(src.overview()), tick(src.orders()), tick(src.live())]);
    (visible ? track(all) : all)
      .then(([ov, os, lv]) => {
        if (n !== seq.current) return; // a newer refresh is on its way
        first.current = false; at.current = Date.now();
        setOverview(ov); setOrders(os); setLive(lv); setError(null); setUpdatedAt(new Date());
      })
      .catch((e: unknown) => { if (n === seq.current) setError(e instanceof Error ? e.message : String((e as { message?: string })?.message ?? e)); })
      .finally(() => setRunning((r) => r - 1));
  }, [src]);
  const reload = useCallback(() => load(false), [load]);
  const retry = useCallback(() => load(true), [load]);

  useEffect(() => {
    reload();
    const off = src.subscribe(reload);
    const t = setInterval(() => { if (!document.hidden) reload(); }, STALE_MS);
    const onBack = () => { if (!document.hidden && Date.now() - at.current > STALE_MS) reload(); };
    document.addEventListener('visibilitychange', onBack);
    addEventListener('online', reload);
    return () => { off(); clearInterval(t); document.removeEventListener('visibilitychange', onBack); removeEventListener('online', reload); };
  }, [src, reload]);

  return { overview, orders, live, error, updatedAt, parts, refreshing: running > 0 && !!overview, failed: !!error && !!overview, retrying: !!error && !overview && running > 0, reload, retry };
}
